import config from '../../config';
import {
  ClaimRequestType,
  ClaimLimitType,
  TravelRequestLineItemSection,
} from './constants/travelreq.enum';
import dayjs, { Dayjs } from 'dayjs';
import { DocumentType, SystemApprovalStatus } from '../../enums/common';
import { ITravelAttachment } from '../../types';
import { SubmissionStatus } from '../../enums';
import {
  TravelHomeDashboard,
  MonthlyClaimItem,
  ApprovalFunnelData,
  ApprovalFunnelStatusKey,
  ITravelListQuery,
  TravelLifecycleStatus,
} from './constants/travelreq.interface';

const DASHBOARD_LIFECYCLE_STATUS: Record<
  ApprovalFunnelStatusKey,
  TravelLifecycleStatus
> = {
  pending: 'PENDING',
  inReview: 'IN_REVIEW',
  hold: 'HOLD',
  awaitingClaim: 'AWAITING_CLAIM',
  approved: 'APPROVED',
  rejected: 'REJECTED',
  cancelled: 'CANCELLED',
};

const TRAVEL_LIFECYCLE_STATUSES = new Set<TravelLifecycleStatus>([
  ...Object.values(DASHBOARD_LIFECYCLE_STATUS),
  'ACTIVE',
  'AWAITING_FINAL_APPROVAL',
  'UNCLASSIFIED',
  'DRAFT',
]);

export type TravelQuickTab =
  | 'all'
  | 'needs_attention'
  | 'drafts'
  | 'pending'
  | 'pending_approvals'
  | 'in_review'
  | 'on_hold'
  | 'awaiting_claim'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'unclassified';

/** Keeps the attachment affordance immediately after the pinned action column. */
export function prioritizeTravelAttachmentColumn<T extends { key: string }>(
  columns: readonly T[],
): T[] {
  const attachmentColumn = columns.find(column => column.key === 'attachment');
  if (!attachmentColumn) return [...columns];

  return [
    attachmentColumn,
    ...columns.filter(column => column !== attachmentColumn),
  ];
}

export function getTravelPinnedLeftColumnIds(options?: {
  includeSelection?: boolean;
  trailing?: string[];
}): string[] {
  return [
    'action',
    ...(options?.includeSelection ? ['select'] : []),
    'hasAttachment',
    'requestNo',
    ...(options?.trailing ?? []),
  ];
}

export function getTravelQuickTabFromLifecycleStatus(
  lifecycleStatus?: TravelLifecycleStatus,
): TravelQuickTab {
  switch (lifecycleStatus) {
    case 'DRAFT':
      return 'drafts';
    case 'IN_REVIEW':
      return 'in_review';
    case 'PENDING':
      return 'pending';
    case 'HOLD':
      return 'on_hold';
    case 'AWAITING_CLAIM':
      return 'awaiting_claim';
    case 'AWAITING_FINAL_APPROVAL':
      return 'pending_approvals';
    case 'APPROVED':
      return 'approved';
    case 'REJECTED':
      return 'rejected';
    case 'CANCELLED':
      return 'cancelled';
    case 'UNCLASSIFIED':
      return 'unclassified';
    default:
      return 'all';
  }
}

export function getTravelLifecycleStatusFromSearchParams(
  searchParams: URLSearchParams,
): TravelLifecycleStatus | undefined {
  const value = searchParams.get('lifecycleStatus') as TravelLifecycleStatus;
  return TRAVEL_LIFECYCLE_STATUSES.has(value) ? value : undefined;
}

export function buildTravelDashboardDrilldownHref(
  pathname: '/travel/all' | '/travel/approvals' | '/travel/reports',
  range: { startDate: Dayjs | null; endDate: Dayjs | null },
  lifecycleStatus?: TravelLifecycleStatus | ApprovalFunnelStatusKey,
): string {
  const searchParams = new URLSearchParams();
  searchParams.set('source', 'travel-dashboard');
  if (range.startDate && range.endDate) {
    searchParams.set('submittedOnFrom', range.startDate.format('YYYY-MM-DD'));
    searchParams.set('submittedOnTo', range.endDate.format('YYYY-MM-DD'));
  } else {
    searchParams.set('dateRange', 'all');
  }

  if (lifecycleStatus) {
    const normalizedStatus =
      DASHBOARD_LIFECYCLE_STATUS[lifecycleStatus as ApprovalFunnelStatusKey] ??
      (lifecycleStatus as TravelLifecycleStatus);
    searchParams.set('lifecycleStatus', normalizedStatus);
  }

  return `${pathname}?${searchParams.toString()}`;
}

export function buildTravelScopedListQuery(
  filters: ITravelListQuery,
  companyGroupId?: number | null,
): ITravelListQuery {
  return {
    ...filters,
    ...(companyGroupId ? { companyGroupId } : {}),
  };
}

export function buildTravelPortfolioCountsQuery(
  filters: ITravelListQuery,
  companyGroupId?: number | null,
): Partial<ITravelListQuery> {
  const {
    page: _page,
    pageSize: _pageSize,
    sortBy: _sortBy,
    needsAttention: _needsAttention,
    lifecycleStatus: _lifecycleStatus,
    ...sharedFilters
  } = filters;

  return Object.fromEntries(
    Object.entries({
      ...sharedFilters,
      ...(companyGroupId ? { companyGroupId } : {}),
    }).filter(
      ([, value]) => value !== '' && value !== null && value !== undefined,
    ),
  ) as Partial<ITravelListQuery>;
}

export const TRAVEL_APPROVAL_QUEUE_STATUSES = new Set<string>([
  SystemApprovalStatus.HOLD,
  SystemApprovalStatus.IN_REVIEW,
  SystemApprovalStatus.PENDING,
]);

export function sanitizeTravelApprovalQueueFilters(
  filters: ITravelListQuery,
): ITravelListQuery {
  const {
    currentApproverId: _currentApproverId,
    isClaimFinalApproved: _isClaimFinalApproved,
    claimFinalApprovedTimeFrom: _claimFinalApprovedTimeFrom,
    claimFinalApprovedTimeTo: _claimFinalApprovedTimeTo,
    finalApprovedById: _finalApprovedById,
    lifecycleStatus: _lifecycleStatus,
    ...compatibleFilters
  } = filters;

  if (!compatibleFilters.approvalStatus) {
    return compatibleFilters;
  }

  const approvalStatus = compatibleFilters.approvalStatus
    .split(',')
    .filter(status => TRAVEL_APPROVAL_QUEUE_STATUSES.has(status))
    .join(',');

  if (!approvalStatus) {
    const { approvalStatus: _approvalStatus, ...filtersWithoutStatus } =
      compatibleFilters;
    return filtersWithoutStatus;
  }

  return { ...compatibleFilters, approvalStatus };
}

export interface TravelSubmissionStrategyInput {
  mode: SubmissionStatus;
  existingSubmissionStatus?: SubmissionStatus;
  recoveryReadiness?: 'COMPLETE' | 'PENDING';
  hasSubmissionAttempt: boolean;
}

export interface TravelSubmissionStrategy {
  useThreePhaseCommit: boolean;
  preserveExistingApprovalDuringRecovery: boolean;
  phaseOneSubmissionStatus: SubmissionStatus;
}

export interface TravelAttachmentRecoveryWarningInput {
  readiness?: 'COMPLETE' | 'PENDING';
  failedCount?: number;
  effectivePipelineStatus?: string;
  isTravelSubmitInProgress: boolean;
  isExplicitRecoveryVisit: boolean;
}

export function shouldShowTravelAttachmentRecoveryWarning({
  readiness,
  failedCount = 0,
  effectivePipelineStatus,
  isTravelSubmitInProgress,
  isExplicitRecoveryVisit,
}: TravelAttachmentRecoveryWarningInput): boolean {
  const hasFailureSignal =
    failedCount > 0 ||
    effectivePipelineStatus === 'ATTACHMENTS_FAILED' ||
    effectivePipelineStatus === 'SUBMISSION_FAILED';

  if (
    isTravelSubmitInProgress &&
    readiness === 'PENDING' &&
    !hasFailureSignal &&
    !isExplicitRecoveryVisit
  ) {
    return false;
  }

  return hasFailureSignal || isExplicitRecoveryVisit;
}

/**
 * Existing submitted requests pre-date the submission-attempt pipeline. They
 * must never be downgraded to draft, and attachment-only recovery must not
 * resubmit or rebuild their approval hierarchy.
 */
export function resolveTravelSubmissionStrategy({
  mode,
  existingSubmissionStatus,
  recoveryReadiness,
  hasSubmissionAttempt,
}: TravelSubmissionStrategyInput): TravelSubmissionStrategy {
  const isSubmit = mode === SubmissionStatus.SUBMITTED;
  const isExistingSubmitted =
    existingSubmissionStatus === SubmissionStatus.SUBMITTED;
  const useThreePhaseCommit = isSubmit && !isExistingSubmitted;
  const preserveExistingApprovalDuringRecovery =
    isSubmit &&
    isExistingSubmitted &&
    recoveryReadiness === 'PENDING' &&
    !hasSubmissionAttempt;

  return {
    useThreePhaseCommit,
    preserveExistingApprovalDuringRecovery,
    phaseOneSubmissionStatus: useThreePhaseCommit
      ? SubmissionStatus.DRAFT
      : mode,
  };
}

export function countPendingAttachmentUploads(
  headerAttachments: ITravelAttachment[],
  attachments: Partial<
    Record<
      TravelRequestLineItemSection,
      Record<string | number, ITravelAttachment[]>
    >
  >,
): number {
  const allAttachments = [
    ...(headerAttachments || []),
    ...Object.values(attachments || {}).flatMap(section =>
      Object.values(section || {}).flat(),
    ),
  ];
  return allAttachments.filter(attachment => {
    const hasPersistedId =
      attachment.id !== undefined &&
      attachment.id !== null &&
      Number.isFinite(Number(attachment.id));
    return (
      !attachment.deleteFlag &&
      !hasPersistedId &&
      Boolean(
        attachment.file ||
          (attachment.galleryFileId && !attachment.galleryMappingId),
      )
    );
  }).length;
}

export function isTravelAttachmentRequired(config: unknown): boolean {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return false;
  }

  const attachments = (config as Record<string, unknown>).attachments;
  return Boolean(
    attachments &&
      typeof attachments === 'object' &&
      !Array.isArray(attachments) &&
      (attachments as Record<string, unknown>).required === true,
  );
}

export function hasAnyActiveTravelAttachment(
  headerAttachments: ITravelAttachment[],
  attachments: Partial<
    Record<
      TravelRequestLineItemSection,
      Record<string | number, ITravelAttachment[]>
    >
  >,
): boolean {
  const isActiveAttachment = (attachment: ITravelAttachment): boolean => {
    const persistedId = Number(attachment.id);
    const hasPersistedId =
      attachment.id !== '' &&
      attachment.id !== null &&
      attachment.id !== undefined &&
      Number.isFinite(persistedId) &&
      persistedId > 0;

    return (
      !attachment.deleteFlag &&
      Boolean(
        attachment.file ||
          attachment.galleryFileId ||
          attachment.filePath ||
          hasPersistedId,
      )
    );
  };

  if ((headerAttachments || []).some(isActiveAttachment)) {
    return true;
  }

  return Object.values(attachments || {}).some(section =>
    Object.values(section || {}).some(items =>
      (items || []).some(isActiveAttachment),
    ),
  );
}

export function isTravelSubmissionMissingRequiredAttachment({
  mode,
  attachmentRequired,
  headerAttachments,
  attachments,
}: {
  mode: SubmissionStatus;
  attachmentRequired: boolean;
  headerAttachments: ITravelAttachment[];
  attachments: Partial<
    Record<
      TravelRequestLineItemSection,
      Record<string | number, ITravelAttachment[]>
    >
  >;
}): boolean {
  return (
    mode === SubmissionStatus.SUBMITTED &&
    attachmentRequired &&
    !hasAnyActiveTravelAttachment(headerAttachments, attachments)
  );
}

export interface TravelWorkflowStatusInput {
  approvalStatus?: string | null;
  isClaimFinalApproved?: boolean;
  isRequestFinalApproved?: boolean;
  isRequestSubmittedForClaimApproval?: boolean;
  requestType?: 'REQUEST' | 'CLAIM' | null;
}

export function getTravelWorkflowStatusLabel({
  approvalStatus,
  isClaimFinalApproved,
  isRequestFinalApproved,
  isRequestSubmittedForClaimApproval,
  requestType,
}: TravelWorkflowStatusInput): string {
  if (isClaimFinalApproved) return 'Claim Approved';
  if (approvalStatus === 'CANCELLED') return 'Cancelled';
  if (approvalStatus === 'REJECTED') return 'Rejected';

  const isRequestPhase =
    requestType === 'REQUEST' && !isRequestSubmittedForClaimApproval;
  const isClaimPhase =
    requestType === 'CLAIM' || isRequestSubmittedForClaimApproval;

  if (isRequestFinalApproved && !isRequestSubmittedForClaimApproval) {
    return 'Travel Approved - Add Expenses';
  }
  if (approvalStatus === 'IN_REVIEW') {
    return isRequestPhase ? 'Travel Request In Review' : 'Claim In Review';
  }
  if (approvalStatus === 'HOLD') {
    return isRequestPhase ? 'Travel Request On Hold' : 'Claim On Hold';
  }
  if (approvalStatus === 'PENDING') {
    return isRequestPhase
      ? 'Travel Request Approval Pending'
      : 'Claim Approval Pending';
  }
  if (isClaimPhase) return 'Claim Approval';
  return approvalStatus
    ? approvalStatus
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, character => character.toUpperCase())
    : '-';
}

interface TravelGalleryMapping {
  id: number;
  fileId: number;
  fileName?: string;
  lineItemId?: number;
  sectionIdentifier?: TravelRequestLineItemSection;
  createdAt?: string;
  createdById?: number;
  createdByUser?: ITravelAttachment['createdByUser'];
  travelAttachmentGallery?: {
    filePath?: string;
    key?: string;
    name?: string;
    remarks?: string;
    documentType?: DocumentType;
  };
}

export function mapTravelGalleryAttachments(
  mappings: TravelGalleryMapping[] | undefined,
): ITravelAttachment[] {
  return (mappings || []).map(mapping => {
    const gallery = mapping.travelAttachmentGallery || {};
    return {
      id: mapping.id,
      galleryMappingId: mapping.id,
      galleryFileId: mapping.fileId,
      galleryFileName: mapping.fileName || gallery.name,
      fileName: mapping.fileName || gallery.name,
      filePath: gallery.filePath,
      key: gallery.key,
      remarks: gallery.remarks || '',
      documentType: gallery.documentType || DocumentType.OTHERS,
      section: mapping.sectionIdentifier || TravelRequestLineItemSection.HEADER,
      lineItemId: mapping.lineItemId,
      createdAt: mapping.createdAt,
      createdById: mapping.createdById,
      createdByUser: mapping.createdByUser,
    };
  });
}

export function mergeTravelGalleryAttachments<
  T extends {
    id: string | number;
    attachments?: ITravelAttachment[];
  },
>(
  items: T[] | undefined,
  galleryAttachments: ITravelAttachment[],
  resolveSection: (item: T) => TravelRequestLineItemSection,
): T[] {
  return (items || []).map(item => ({
    ...item,
    attachments: [
      ...(item.attachments || []),
      ...galleryAttachments.filter(
        attachment =>
          attachment.section === resolveSection(item) &&
          String(attachment.lineItemId) === String(item.id),
      ),
    ],
  }));
}

export interface ResolvedClaimType {
  claimType: string | null;
  claimCategoryId: number;
  claimCategoryName: string;
  claimSubCategoryId: number;
  claimSubCategoryName: string;
}

export function getClaimTypeFromCategories(
  claimCategory: number,
  claimSubCategory: number,
): ClaimRequestType {
  if (config.modules.CLM?.type) {
    const envConfig = config.modules.CLM.type;
    const tourItem = envConfig.tour.find(
      item =>
        item.claimCategory == claimCategory &&
        item.claimSubCategory == claimSubCategory,
    );
    if (tourItem) {
      return ClaimRequestType.TRAVEL;
    }
    const handsetItem = envConfig.handset.find(
      item =>
        item.claimCategory == claimCategory &&
        item.claimSubCategory == claimSubCategory,
    );
    if (handsetItem) {
      return ClaimRequestType.HANDSET;
    }
    const conveyanceItem = envConfig.conveyance.find(
      item =>
        item.claimCategory == claimCategory &&
        item.claimSubCategory == claimSubCategory,
    );

    if (conveyanceItem) {
      return ClaimRequestType.CONVEYANCE;
    }
    const directClaimItem = envConfig.directClaim.find(
      item =>
        item.claimCategory == claimCategory &&
        item.claimSubCategory == claimSubCategory,
    );
    if (directClaimItem) {
      return ClaimRequestType.CLAIM;
    }
  }

  return null;
}

export function getClaimTypeFromResolvedConfig(
  claimTypes: ResolvedClaimType[],
  claimCategoryId: number,
  claimSubCategoryId: number,
): string | null {
  const match = claimTypes.find(
    item =>
      item.claimCategoryId === +claimCategoryId &&
      item.claimSubCategoryId === +claimSubCategoryId,
  );
  return match?.claimType ?? null;
}

export const checkIfExpenseSectionShouldBeVisible = (
  data: any,
  user: any,
  formConfig: any,
) => {
  const { claimCategory, claimSubCategory } = data;
  const claimType = getClaimTypeFromCategories(claimCategory, claimSubCategory);
  let visible = formConfig.expenseClaimForm?.visible;

  if (claimType === ClaimRequestType.TRAVEL) {
    if (!data?.isRequestFinalApproved) {
      visible = false;
    }
  }
  return visible;
};

export const checkIfOfficeExpenseSectionShouldBeVisible = (
  data: any,
  user: any,
  formConfig: any,
) => {
  const { claimCategory, claimSubCategory } = data;
  const claimType = getClaimTypeFromCategories(claimCategory, claimSubCategory);
  const visible = formConfig.expenseClaimForm?.visible;

  // if (claimType === ClaimRequestType.TRAVEL) {
  // visible = false;
  // }
  return visible;
};

// Calculate proposed amount based on date range and claimLimitType
export const calculateProposedAmount = (
  baseAmt: number | null,
  limitType: ClaimLimitType | null,
  fromDate: any,
  toDate: any,
): number | null => {
  if (!baseAmt || !limitType || !fromDate || !toDate) {
    return baseAmt;
  }

  const start = dayjs(fromDate);
  const end = dayjs(toDate);

  switch (limitType) {
    case ClaimLimitType.PER_DAY: {
      const days = end.diff(start, 'day') + 1;
      return baseAmt * days;
    }
    case ClaimLimitType.PER_MONTH: {
      const months = end.diff(start, 'month', true);
      // Round up to nearest month if there's a partial month
      const roundedMonths = Math.ceil(months);
      return baseAmt * roundedMonths;
    }
    case ClaimLimitType.PER_QUARTER: {
      const months = end.diff(start, 'month', true);
      const quarters = Math.ceil(months / 3);
      return baseAmt * quarters;
    }
    case ClaimLimitType.PER_YEAR: {
      const years = end.diff(start, 'year', true);
      const roundedYears = Math.ceil(years);
      return baseAmt * roundedYears;
    }
    default:
      return baseAmt;
  }
};

export interface OverviewTileItem {
  label: string;
  value: number;
  amount?: number;
  count?: number;
  isAmountPrimary?: boolean;
  color: string;
  lifecycleStatus?: TravelLifecycleStatus;
}

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    notation: amount >= 10_000_000 ? 'compact' : 'standard',
  }).format(amount);
};

export function getTravelDashboardOverviewTiles(
  summaryData?: TravelHomeDashboard['summary'],
): OverviewTileItem[] {
  const cards = summaryData?.cards;

  // Active submitted requests (drafts are intentionally a separate card)
  const totalRequestCount =
    cards?.totalRequests?.count ?? summaryData?.totalSubmittedRequests ?? 0;
  const totalRequestAmount =
    cards?.totalRequests?.amount ?? summaryData?.totalSubmittedAmount ?? 0;

  // Submitted
  const submittedCount =
    cards?.submitted?.count ??
    summaryData?.totalSubmittedPendingApprovedRequests ??
    0;
  const submittedAmount =
    cards?.submitted?.amount ??
    summaryData?.submittedAmount ??
    summaryData?.totalSubmittedAmount ??
    0;

  // Drafts
  const draftCount =
    cards?.drafts?.count ?? summaryData?.totalDraftRequests ?? 0;
  const draftAmount = cards?.drafts?.amount ?? summaryData?.draftAmount ?? 0;

  // Final Approved
  const finalApprovedCount =
    cards?.finalApproved?.count ?? summaryData?.totalFinalApprovedRequests ?? 0;
  const finalApprovedAmount =
    cards?.finalApproved?.amount ?? summaryData?.approvedAmount ?? 0;

  // Claim Value
  const claimValueAmount =
    cards?.claimValue?.amount ??
    summaryData?.totalSubmittedAmount ??
    summaryData?.approvedAmount ??
    0;
  const claimValueCount = cards?.claimValue?.count;

  return [
    {
      label: 'Active Claims',
      value: totalRequestCount,
      amount: totalRequestAmount,
      color: '#2563EB',
      lifecycleStatus: 'ACTIVE',
    },
    {
      label: 'Awaiting Final Approval',
      value: submittedCount,
      amount: submittedAmount,
      color: '#059669',
      lifecycleStatus: 'AWAITING_FINAL_APPROVAL',
    },
    {
      label: 'My Drafts',
      value: draftCount,
      amount: draftAmount,
      color: '#9333EA',
      lifecycleStatus: 'DRAFT',
    },
    {
      label: 'Final Approved',
      value: finalApprovedCount,
      amount: finalApprovedAmount,
      color: '#10B981',
      lifecycleStatus: 'APPROVED',
    },
    {
      label: 'Total Claim Value',
      value: claimValueAmount,
      count: claimValueCount,
      isAmountPrimary: true,
      color: '#D97706',
      lifecycleStatus: 'ACTIVE',
    },
  ];
}

export function formatMonthlyClaimsData(
  rawList: MonthlyClaimItem[] | undefined,
  currentFYMonths: string[],
) {
  const apiDataMap = new Map<
    string,
    {
      submittedAmount: number;
      approvedAmount: number;
      awaitingClaimAmount: number;
      totalAmount: number;
      claimedAmount: number;
      submittedCount: number;
      approvedCount: number;
      awaitingClaimCount: number;
      totalCount: number;
    }
  >(
    (rawList || []).map(
      item =>
        [
          item.month,
          {
            submittedAmount: item.submittedAmount ?? 0,
            approvedAmount: item.approvedAmount ?? item.amount ?? 0,
            awaitingClaimAmount: item.awaitingClaimAmount ?? 0,
            totalAmount:
              item.totalAmount ??
              (item.submittedAmount ?? 0) +
                (item.approvedAmount ?? item.amount ?? 0) +
                (item.awaitingClaimAmount ?? 0),
            claimedAmount:
              item.claimedAmount ?? item.totalAmount ?? item.amount ?? 0,
            submittedCount: item.submittedCount ?? 0,
            approvedCount: item.approvedCount ?? item.count ?? 0,
            awaitingClaimCount: item.awaitingClaimCount ?? 0,
            totalCount:
              item.totalCount ??
              (item.submittedCount ?? 0) +
                (item.approvedCount ?? item.count ?? 0) +
                (item.awaitingClaimCount ?? 0),
          },
        ] as const,
    ),
  );

  return currentFYMonths.map(monthStr => {
    const data = apiDataMap.get(monthStr);
    return {
      month: monthStr,
      submittedAmount: data?.submittedAmount ?? 0,
      approvedAmount: data?.approvedAmount ?? 0,
      awaitingClaimAmount: data?.awaitingClaimAmount ?? 0,
      totalAmount: data?.totalAmount ?? 0,
      claimedAmount: data?.claimedAmount ?? 0,
      submittedCount: data?.submittedCount ?? 0,
      approvedCount: data?.approvedCount ?? 0,
      awaitingClaimCount: data?.awaitingClaimCount ?? 0,
      totalCount: data?.totalCount ?? 0,
    };
  });
}

export interface StatusCardMetrics {
  statusKey: ApprovalFunnelStatusKey;
  approvalStatus: string;
  count: number;
  amount: number;
  color: string;
  hint: string;
}

export function getTravelApprovalStatusCards(
  funnelData: ApprovalFunnelData | undefined,
  dashboardFunnel?: ApprovalFunnelData,
): StatusCardMetrics[] {
  const sections = funnelData?.sections ?? dashboardFunnel?.sections ?? {};
  const amounts = funnelData?.amounts ?? dashboardFunnel?.amounts ?? {};
  const legacyAmountKeys: Partial<
    Record<ApprovalFunnelStatusKey, keyof ApprovalFunnelData>
  > = {
    pending: 'pendingAmount',
    inReview: 'inReviewAmount',
    approved: 'approvedAmount',
    rejected: 'rejectedAmount',
    cancelled: 'cancelledAmount',
    awaitingClaim: 'awaitingClaimAmount',
  };

  const extractMetrics = (key: ApprovalFunnelStatusKey) => {
    const sectionObj = sections[key];
    const sectionMetrics =
      typeof sectionObj === 'object' ? sectionObj : undefined;
    const legacyAmountKey = legacyAmountKeys[key];
    const funnelLegacyAmount = legacyAmountKey
      ? funnelData?.[legacyAmountKey]
      : undefined;
    const dashboardLegacyAmount = legacyAmountKey
      ? dashboardFunnel?.[legacyAmountKey]
      : undefined;
    const countVal =
      sectionMetrics?.count !== undefined
        ? sectionMetrics.count
        : typeof funnelData?.[key] === 'number'
          ? funnelData[key]
          : typeof dashboardFunnel?.[key] === 'number'
            ? dashboardFunnel[key]
            : 0;

    const amountVal =
      sectionMetrics?.amount !== undefined
        ? sectionMetrics.amount
        : typeof amounts[key] === 'number'
          ? amounts[key]
          : typeof funnelLegacyAmount === 'number'
            ? funnelLegacyAmount
            : typeof dashboardLegacyAmount === 'number'
              ? dashboardLegacyAmount
              : 0;

    return { count: countVal ?? 0, amount: amountVal ?? 0 };
  };

  const pending = extractMetrics('pending');
  const approved = extractMetrics('approved');
  const rejected = extractMetrics('rejected');
  const inReview = extractMetrics('inReview');
  const cancelled = extractMetrics('cancelled');
  const hold = extractMetrics('hold');
  const awaitingClaim = extractMetrics('awaitingClaim');

  return [
    {
      statusKey: 'pending',
      approvalStatus: 'Pending',
      count: pending.count,
      amount: pending.amount,
      color: '#f59e0b',
      hint: 'Pending approval',
    },
    {
      statusKey: 'inReview',
      approvalStatus: 'In Review',
      count: inReview.count,
      amount: inReview.amount,
      color: '#6366f1',
      hint: 'Under review',
    },
    {
      statusKey: 'hold',
      approvalStatus: 'On Hold',
      count: hold.count,
      amount: hold.amount,
      color: '#d97706',
      hint: 'Awaiting clarification',
    },
    {
      statusKey: 'awaitingClaim',
      approvalStatus: 'Awaiting Claim',
      count: awaitingClaim.count,
      amount: awaitingClaim.amount,
      color: '#0891b2',
      hint: 'Add and submit expenses',
    },
    {
      statusKey: 'approved',
      approvalStatus: 'Approved',
      count: approved.count,
      amount: approved.amount,
      color: '#10b981',
      hint: 'Final Approved',
    },
    {
      statusKey: 'rejected',
      approvalStatus: 'Rejected',
      count: rejected.count,
      amount: rejected.amount,
      color: '#ef4444',
      hint: 'Rejected requests',
    },
    {
      statusKey: 'cancelled',
      approvalStatus: 'Cancelled',
      count: cancelled.count,
      amount: cancelled.amount,
      color: '#6b7280',
      hint: 'Cancelled claims',
    },
  ];
}
